import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { ButtonLoader } from "Components";
import { newAuthService } from "Service/Axios";
import { saveUserProfileUrl } from "Constant/api";
import { VIEW_PROFILE_SECTIONS } from "Constant/constants";
import { Validator } from "Utils/validator";

export const EducationModal = ({ show, educationData, toggle, fetchData }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    gid: "",
    degree: "",
    major: "",
    institution: "",
    startDate: "",
    endDate: "",
    location: "",
    gpa: "",
  });
  const [errMsg, setErrMsg] = useState({
    degree: "",
    major: "",
    institution: "",
    startDate: "",
    endDate: "",
    location: "",
  });

  const { gid, degree, major, institution, startDate, endDate, location, gpa } =
    formData;
  const validationRequiredFields = [
    "degree",
    "major",
    "institution",
    "startDate",
    "endDate",
    "location",
  ];

  useEffect(() => {
    if (educationData) {
      setFormData({
        gid: educationData.gid || "",
        degree: educationData.degree || "",
        major: educationData.major || "",
        institution: educationData.institution || "",
        startDate: educationData.startDate || "",
        endDate: educationData.endDate || "",
        location: educationData.location || "",
        gpa: educationData.gpa || "",
      });
    }
  }, [educationData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (!Validator.text(value)) {
      setErrMsg((prev) => ({
        ...prev,
        [name]: t(`common.messages.error.viewProfile.education.${name}.blank`),
      }));
    } else {
      setErrMsg((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const getValidation = () => {
    return new Promise((resolve) => {
      let isValid = true;
      const errors = {};

      for (const field of validationRequiredFields) {
        const value = formData[field];

        if (!Validator.text(value)) {
          errors[field] = t(
            `common.messages.error.viewProfile.education.${field}.blank`
          );
          isValid = false;
        }
      }

      setErrMsg((prev) => ({
        ...prev,
        ...errors,
      }));

      resolve(isValid);
    });
  };

  const onSaveEducation = async () => {
    const validationStatus = await getValidation();
    if (validationStatus) {
      try {
        const payload = {
          key: VIEW_PROFILE_SECTIONS.education,
          [VIEW_PROFILE_SECTIONS.education]: {
            degree,
            major,
            institution,
            startDate,
            endDate,
            location,
            gpa,
          },
        };

        if (gid.trim()) {
          payload[VIEW_PROFILE_SECTIONS.education].gid = gid;
        }

        setLoading(true);
        const response = await newAuthService(
          saveUserProfileUrl,
          "POST",
          payload
        );
        if (response.status) {
          toast.success(
            t("common.messages.notification.success.profileUpdated")
          );
          fetchData();
          toggle();
        } else {
          toast.error(response.message);
        }
      } catch {
        toast.error(
          t("common.messages.notification.failed.somethingWentWrong")
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal show={show} onHide={toggle} className="highlightsModal">
      <Modal.Header closeButton>
        <Modal.Title className="text-md font-medium">
          {t(`auth.viewProfile.${gid ? "editEducation" : "addEducation"}`)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="degree" className="form-label">
                {t("common.label.degree")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="degree"
                name="degree"
                placeholder={t("common.placeHolder.degree")}
                value={degree}
                onChange={handleChange}
              />
              {errMsg.degree && <p className="error">{errMsg.degree}</p>}
            </div>
          </div>
          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="major" className="form-label">
                {t("common.label.major")} <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="major"
                name="major"
                placeholder={t("common.placeHolder.major")}
                value={major}
                onChange={handleChange}
              />
              {errMsg.major && <p className="error">{errMsg.major}</p>}
            </div>
          </div>

          <div className="col-md-12 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="institution" className="form-label">
                {t("common.label.institution")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="institution"
                name="institution"
                placeholder={t("common.placeHolder.institution")}
                value={institution}
                onChange={handleChange}
              />
              {errMsg.institution && (
                <p className="error">{errMsg.institution}</p>
              )}
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="startDate" className="form-label">
                {t("common.label.startDate")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                id="startDate"
                name="startDate"
                placeholder={t("common.placeHolder.startDate")}
                value={startDate}
                onChange={handleChange}
              />
              {errMsg.startDate && <p className="error">{errMsg.startDate}</p>}
            </div>
          </div>
          <div className="col-md-6 col-sm-6 col-12">
            <div className="mb-3">
              <label htmlFor="endDate" className="form-label">
                {t("common.label.endDate")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                id="endDate"
                name="endDate"
                placeholder={t("common.placeHolder.endDate")}
                value={endDate}
                onChange={handleChange}
              />
              {errMsg.endDate && <p className="error">{errMsg.endDate}</p>}
            </div>
          </div>
          <div className="col-md-6 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="location" className="form-label">
                {t("common.label.location")}{" "}
                <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="location"
                name="location"
                placeholder={t("common.placeHolder.location")}
                value={location}
                onChange={handleChange}
              />
              {errMsg.location && <p className="error">{errMsg.location}</p>}
            </div>
          </div>
          <div className="col-md-6 col-sm-12 col-12">
            <div className="mb-3">
              <label htmlFor="gpa" className="form-label">
                {t("common.label.gpa")}
              </label>
              <input
                type="text"
                className="form-control"
                id="gpa"
                name="gpa"
                placeholder={t("common.placeHolder.gpa")}
                value={gpa}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={toggle}>
          {t("common.button.cancel")}
        </Button>
        {loading ? (
          <ButtonLoader />
        ) : (
          <Button variant="primary" onClick={onSaveEducation}>
            {t("common.button.save")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};
